import { useEffect, useMemo, useRef, useState } from 'react'
import * as chrono from 'chrono-node'

interface ReminderPickerProps {
  onSelect: (date: Date) => void
  onClose: () => void
}

export default function ReminderPicker({ onSelect, onClose }: ReminderPickerProps) {
  const [input, setInput] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  // Close on outside click
  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  // Parse natural language input
  const parsedDate = useMemo(() => {
    if (!input.trim()) return null
    const parsed = chrono.parse(input, new Date(), { forwardDate: true })
    if (!parsed.length || !parsed[0].start) return null
    const result = parsed[0].start.date()
    // If chrono didn't find a time component, default to 8 AM
    const hasTime = parsed[0].start.isCertain('hour') || parsed[0].start.isCertain('minute')
    if (!hasTime) {
      result.setHours(8, 0, 0, 0)
    }
    // Must be in the future
    if (result.getTime() <= Date.now()) return null
    return result
  }, [input])

  const presets = useMemo(() => {
    const now = new Date()
    const options: { label: string; detail: string; date: Date }[] = []

    // Later today (6 PM)
    if (now.getHours() < 18) {
      const d = new Date(now)
      d.setHours(18, 0, 0, 0)
      options.push({ label: 'Later today', detail: formatDetail(d), date: d })
    }

    // Tonight (8 PM)
    if (now.getHours() < 20) {
      const d = new Date(now)
      d.setHours(20, 0, 0, 0)
      options.push({ label: 'Tonight', detail: formatDetail(d), date: d })
    }

    // Tomorrow 8 AM
    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(8, 0, 0, 0)
    options.push({ label: 'Tomorrow', detail: formatDetail(tomorrow), date: tomorrow })

    // In 3 days
    const in3 = new Date(now)
    in3.setDate(in3.getDate() + 3)
    in3.setHours(8, 0, 0, 0)
    options.push({ label: 'In 3 days', detail: formatDetail(in3), date: in3 })

    // This weekend (Saturday 8 AM)
    const dayOfWeek = now.getDay() // 0=Sun, 6=Sat
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      const sat = new Date(now)
      sat.setDate(sat.getDate() + (6 - dayOfWeek))
      sat.setHours(8, 0, 0, 0)
      options.push({ label: 'This weekend', detail: formatDetail(sat), date: sat })
    }

    // Next week (Monday 8 AM)
    const nextMon = new Date(now)
    nextMon.setDate(nextMon.getDate() + ((8 - nextMon.getDay()) % 7 || 7))
    nextMon.setHours(8, 0, 0, 0)
    options.push({ label: 'Next week', detail: formatDetail(nextMon), date: nextMon })

    // In a month
    const month = new Date(now)
    month.setDate(month.getDate() + 30)
    month.setHours(8, 0, 0, 0)
    options.push({ label: 'In a month', detail: formatDetail(month), date: month })

    return options.filter((o) => o.date.getTime() > now.getTime())
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && parsedDate) {
      onSelect(parsedDate)
    }
  }

  return (
    <div
      ref={containerRef}
      className="absolute right-0 top-full z-30 mt-1 w-72 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-lg"
    >
      {/* Natural language input */}
      <div className="border-b border-zinc-100 px-3 py-2.5">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Try: 2pm, in 3 days, friday, aug 15"
          className="w-full text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
        />
      </div>

      {/* Parsed result */}
      {parsedDate && (
        <button
          onClick={() => onSelect(parsedDate)}
          className="flex w-full items-center gap-2.5 bg-blue-50 px-3 py-2.5 text-left hover:bg-blue-100"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          <div>
            <p className="text-xs font-medium text-blue-700">{formatDetail(parsedDate)}</p>
          </div>
        </button>
      )}

      {/* Presets */}
      <div className="max-h-64 overflow-y-auto">
        {presets.map((preset) => (
          <button
            key={preset.label}
            onClick={() => onSelect(preset.date)}
            className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-zinc-50"
          >
            <span className="text-xs font-medium text-zinc-700">{preset.label}</span>
            <span className="text-[11px] text-zinc-400">{preset.detail}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function formatDetail(date: Date): string {
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const dayStr = date.toDateString() === now.toDateString()
    ? 'Today'
    : date.toDateString() === tomorrow.toDateString()
      ? 'Tomorrow'
      : date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })

  const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  return `${dayStr} ${timeStr}`
}
